import PdImage from '@/components/pd-image/pd-image'
import PdSwiper from '@/components/pd-swiper/pd-swiper'
import clsx from 'clsx'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Swiper as SwiperClass } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useMobileDetector } from '@/hooks/use-mobile-detector'
import { AboutUsData } from '@/service/about-us.service'

interface HistoryProps {
  data: {
    title?: string
    history?: AboutUsData['devHistory']
  }
  isBlueTheme?: boolean
}

const History: FC<HistoryProps> = ({ data, isBlueTheme }) => {
  const yearsSwiperRef = useRef<SwiperClass | null>(null)
  const contentSwiperRef = useRef<SwiperClass | null>(null)
  const [activeYearIndex, setActiveYearIndex] = useState(0)
  const isMobileDevice = useMobileDetector()

  useEffect(() => {
    if (isMobileDevice) return
    if (isBlueTheme) {
      contentSwiperRef.current?.autoplay.start()
    } else {
      contentSwiperRef.current?.autoplay.stop()
    }
  }, [isBlueTheme, isMobileDevice])

  const years = useMemo(() => data.history?.map((item) => item.year) || [], [data.history])

  return (
    <div
      className={clsx(
        'w-full h-fit',
        'py-20 laptop:py-40',
        'flex flex-col',
        'transition-all duration-600 ease-in-out',
        isBlueTheme ? 'bg-pdblue' : 'bg-white'
      )}
    >
      <h1
        className={clsx(
          'max-w-300 mx-auto',
          'px-6 laptop:px-0',
          'text-center font-semibold',
          'text-3xl laptop:text-4xl',
          'transition-all duration-600 ease-in-out',
          isBlueTheme ? 'text-white' : 'text-black'
        )}
      >
        {data.title}
      </h1>
      <div className={clsx('px-0 laptop:px-20')}>
        <div className={clsx('w-full relative', 'my-3 laptop:mt-6 laptop:mb-20')}>
          {years.length > 1 && (
            <div
              className={clsx(
                'h-full absolute left-0 inset-0 z-10',
                'w-6 laptop:w-12',
                'bg-gradient-to-r to-transparent',
                'transition-all duration-600 ease-in-out',
                isBlueTheme ? 'from-pdblue' : 'from-white'
              )}
            >
              <div
                className={clsx(
                  'pd-navigation-prev',
                  'w-6 h-6 -mt-3',
                  'laptop:w-12 laptop:h-12 laptop:-mt-6',
                  !isBlueTheme && 'bg-[url(/icons/icon_angle_prev.svg)]',
                  'hover:[background-image:url(/icons/swiper/default.svg)]',
                  isBlueTheme ? 'hover:bg-white/20' : 'hover:bg-pdblue',
                  activeYearIndex === 0 ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
                onClick={() => {
                  yearsSwiperRef.current?.slidePrev()
                }}
              ></div>
            </div>
          )}
          <Swiper
            loop={false}
            slidesPerView={isMobileDevice ? 2 : 3}
            spaceBetween={isMobileDevice ? 50 : 300}
            onSwiper={(swiper) => {
              yearsSwiperRef.current = swiper
            }}
            centeredSlides={true}
            onSlideChange={(swiper) => {
              setActiveYearIndex(swiper.activeIndex)
              contentSwiperRef.current?.slideTo(swiper.activeIndex)
            }}
            slideToClickedSlide={true}
            className="select-none"
          >
            {years.map((year, index) => {
              return (
                <SwiperSlide
                  key={year + index}
                  className={clsx(
                    'flex justify-center cursor-pointer',
                    'after:absolute after:inset-0',
                    'after:w-[calc(100%+50px)] after:-left-[25px] laptop:after:w-[calc(100%+300px)] laptop:after:-left-[150px]'
                  )}
                >
                  <p
                    className={clsx(
                      'text-64 laptop:text-9xl font-semibold',
                      'transition-all duration-600 ease-in-out',
                      activeYearIndex === index
                        ? isBlueTheme
                          ? 'text-white'
                          : 'text-black'
                        : isBlueTheme
                          ? 'text-white/40'
                          : 'text-black/20'
                    )}
                  >
                    {year}
                  </p>
                </SwiperSlide>
              )
            })}
          </Swiper>
          <div
            className={clsx(
              'h-full absolute right-0 bottom-0 z-10',
              'w-6 laptop:w-12',
              'bg-gradient-to-r from-transparent',
              'transition-all duration-600 ease-in-out',
              isBlueTheme ? 'to-pdblue' : 'to-white'
            )}
          >
            <div
              className={clsx(
                'pd-navigation-next',
                'w-6 h-6 -mt-3',
                'laptop:w-12 laptop:h-12 laptop:-mt-6',
                !isBlueTheme && 'bg-[url(/icons/icon_angle_prev.svg)]',
                'hover:[background-image:url(/icons/swiper/default.svg)]',
                isBlueTheme ? 'hover:bg-white/20' : 'hover:bg-pdblue',
                activeYearIndex >= years.length - 1 ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
              onClick={() => {
                yearsSwiperRef.current?.slideNext()
              }}
            ></div>
          </div>
        </div>
      </div>
      <PdSwiper
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={!isMobileDevice}
        autoplayDelay={5000}
        loop={false}
        slidesPerView={1}
        hidePagination
        hideNavigation
        onSwiper={(swiper) => {
          contentSwiperRef.current = swiper
          swiper.autoplay.stop()
        }}
        onSlideChange={(swiper) => {
          setActiveYearIndex(swiper.activeIndex)
          yearsSwiperRef.current?.slideTo(swiper.activeIndex)
        }}
        autoHeight
      >
        {data.history?.map((item) => {
          return (
            <SwiperSlide key={item.year}>
              <div
                className={clsx(
                  'flex gap-x-6',
                  'gap-y-5 laptop:gap-y-0',
                  'w-full max-w-300 mx-auto',
                  'flex-col laptop:flex-row',
                  'px-6 laptop:px-0'
                )}
              >
                <div className={clsx('shrink-0', 'w-full [aspect-ratio:3/2] laptop:w-150 laptop:h-100')}>
                  <PdImage src={item.mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
                </div>

                <ul className={clsx('flex flex-col justify-center', 'gap-y-3 laptop:gap-y-2')}>
                  {item.monthDesc.map((event, index) => {
                    return (
                      event.title && (
                        <li key={event.title + index} className="flex items-center gap-x-2">
                          <div
                            className={clsx(
                              'px-2 py-3px rounded-sm flex-none min-w-12',
                              'text-sm font-semibold text-center',
                              isBlueTheme ? 'bg-white/20 text-white/90' : 'bg-pdbg text-black/90'
                            )}
                          >
                            {event.title}
                          </div>
                          <p
                            className={clsx(
                              'text-sm whitespace-pre-wrap',
                              isBlueTheme ? 'text-white/90 ' : 'text-black/90'
                            )}
                          >
                            {event.desc}
                          </p>
                        </li>
                      )
                    )
                  })}
                </ul>
              </div>
            </SwiperSlide>
          )
        })}
      </PdSwiper>
    </div>
  )
}

export default History
